import React, { useState, useEffect, useRef } from 'react';
import { Grid, Stack, Box } from '@mui/material';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { renderWeekPickerDay } from '../../common/ui/qvshop/components/utils/DatePickerUtils';
import { getPickersLocale } from '../../common/ui/qvshop/components/utils/DatePickerLocale';
import { getAvailabilityCalendarForMonth, selectProduct } from '../../common/store/productSlice';
import { selectLocation } from '../../common/store/filtersSlice';
import DateInfoPopover from './DateInfoPopover';

import QVContainer from '../../common/ui/qvshop/components/QVContainer';

import { selectShowReservationsOnHoverInAvailabilityCalendar } from '../../common/store/settingsSlice';

// Custom styled DateCalendar to make selection transparent
const StyledDateCalendar = styled(DateCalendar)(({ theme }) => ({
    '& .MuiPickersDay-root.Mui-selected': {
        backgroundColor: 'transparent',
        border: 'none'
    },
    '& .MuiPickersDay-root:hover': {
        backgroundColor: 'transparent',
    },
    '& .MuiPickersDay-root.Mui-selected:hover': {
        backgroundColor: 'transparent',
    },
    '& .MuiPickersDay-root.Mui-selected:focus': {
        backgroundColor: 'transparent'
    },
    '& .MuiPickersDay-root:focus, & .MuiPickersDay-root.Mui-focused': {
        backgroundColor: 'transparent'
    },
    '& .MuiPickersDay-root.Mui-highlighted': {
        backgroundColor: 'transparent'
    },
    '& .MuiPickersDay-today': {
        border: 'none'
    }
}));

const AvailabilityCalendar = () => {
    const product = useSelector(selectProduct);
    const dispatch = useDispatch();
    const { i18n } = useTranslation();
    const pickersLocale = getPickersLocale(i18n.language);

    const showReservationsOnHoverInAvailabilityCalendar = useSelector(selectShowReservationsOnHoverInAvailabilityCalendar);
    const location = useSelector(selectLocation);

    const [currentMonth, setCurrentMonth] = useState(dayjs().startOf('month'));
    const nextMonth = currentMonth.add(1, 'month');

    // Track the month/year values to prevent unnecessary re-fetches
    const [fetchedLeftMonths, setFetchedLeftMonths] = useState({});
    const [fetchedRightMonths, setFetchedRightMonths] = useState({});

    const [hoveredDay, setHoveredDay] = useState(null);
    const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
    const calendarRef = useRef(null);

    const shouldDisableDate = (dayToCheck) => {
        return false;
    };

    const handleDayHoverOut = () => {
        setHoveredDay(null);
    }

    const segmentsForDay = (day) => {
        if(!day || !product) {
            return [];
        }
        const formattedDate = day.format("YYYY-MM-DD");
        const monthData = day.month() === currentMonth.month() && day.year() === currentMonth.year()
            ? product?.month_left?.[product.product_id]
            : product?.month_right?.[product.product_id];
        return monthData?.[formattedDate]?.segments || [];
    };

    const handleDayHover = (day, event) => {
        const hasSegments = segmentsForDay(day).length > 0;
        if (!showReservationsOnHoverInAvailabilityCalendar && !hasSegments) {
            return;
        }

        if (event) {
            // Calculate position for the popover
            const rect = event.currentTarget.getBoundingClientRect();
            const calendarRect = calendarRef.current.getBoundingClientRect();
            
            // Position the popover to the right and slightly below the day cell
            setPopoverPosition({
                x: rect.left - calendarRect.left + 40, // Position to the right of the day cell
                y: rect.top - calendarRect.top + 40     // Slightly above the top of the day cell
            });

            setHoveredDay(day);
        }
    };

    const segmentsForHoveredDay = segmentsForDay(hoveredDay);
    
    useEffect(() => {
        if (product) {
            // Calendar API data depends on product, location, month and year.
            const currentMonthKey = `${product.product_id}-${location}-${currentMonth.format('MM')}-${currentMonth.format('YYYY')}`;
            
            // Only fetch if we haven't already fetched this month for the left calendar
            if (!fetchedLeftMonths[currentMonthKey]) {
                console.log('Fetching left calendar for:', currentMonthKey);

                dispatch(getAvailabilityCalendarForMonth({
                    month: currentMonth.format('MM'),
                    year: currentMonth.format('YYYY'),
                    product: product,
                    field: 'month_left'
                }));
                
                // Mark this month as fetched for left calendar
                setFetchedLeftMonths(prev => ({
                    ...prev,
                    [currentMonthKey]: true
                }));
            }
        }
    }, [dispatch, product, location, currentMonth, fetchedLeftMonths]);

    useEffect(() => {
        if (product) {
            // Calendar API data depends on product, location, month and year.
            const nextMonthKey = `${product.product_id}-${location}-${nextMonth.format('MM')}-${nextMonth.format('YYYY')}`;
            
            // Only fetch if we haven't already fetched this month for the right calendar
            if (!fetchedRightMonths[nextMonthKey]) {
                console.log('Fetching right calendar for:', nextMonthKey);

                dispatch(getAvailabilityCalendarForMonth({
                    month: nextMonth.format('MM'),
                    year: nextMonth.format('YYYY'),
                    product: product,
                    field: 'month_right'
                }));
                
                // Mark this month as fetched for right calendar
                setFetchedRightMonths(prev => ({
                    ...prev,
                    [nextMonthKey]: true
                }));
            }
        }
    }, [dispatch, product, location, nextMonth, fetchedRightMonths]);

    // hide the navigation buttons
    const HiddenNavigation = () => null;

    if(!product) {
        return null;
    }

    return (
        <QVContainer borderRadius={8}>
            <Stack direction='column' spacing={2}>
                <Box ref={calendarRef} position="relative" onMouseLeave={handleDayHoverOut}>
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale={pickersLocale.adapterLocale}
                        localeText={pickersLocale.localeText}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <StyledDateCalendar 
                                    value={currentMonth}
                                    views={['day']}
                                    readOnly
                                    slots={{
                                        leftArrowIcon: HiddenNavigation,
                                        rightArrowIcon: HiddenNavigation,
                                        day: (props) => renderWeekPickerDay(
                                            props, 
                                            true, 
                                            product?.month_left?.[product.product_id] || {}, 
                                            shouldDisableDate,
                                            handleDayHover,
                                            handleDayHoverOut,
                                            { availabilityDisplayMode: 'segments' }
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <StyledDateCalendar 
                                    value={nextMonth}
                                    views={['day']}
                                    readOnly
                                    onMonthChange={(newMonth) => {
                                        // when the second calendar month changes, update the first calendar
                                        const updatedCurrentMonth = newMonth.subtract(1, 'month');
                                        setCurrentMonth(updatedCurrentMonth);
                                    }}
                                    slots={{
                                        day: (props) => renderWeekPickerDay(
                                            props, 
                                            true, 
                                            product?.month_right?.[product.product_id] || {}, 
                                            shouldDisableDate,
                                            handleDayHover,
                                            handleDayHoverOut,
                                            { availabilityDisplayMode: 'segments' }
                                        )
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                    
                    <DateInfoPopover 
                        day={hoveredDay} 
                        position={popoverPosition}
                        isVisible={hoveredDay !== null}
                        segments={segmentsForHoveredDay}
                    />
                </Box>
            </Stack>
        </QVContainer>
    );
};

export default AvailabilityCalendar;
